/**
 * Field accessors for Propeller SDK v2 entities.
 *
 * These replace the v1 `@propeller-commerce/propeller` getters AND the
 * hand-rolled Phase-1 interfaces. Types now come straight from the SDK
 * (`@propeller-commerce/propeller-sdk-v2`); the getter functions keep their
 * exact names and return shapes so the ~26 call sites stay drop-in.
 *
 * Behavioural quirks preserved on purpose:
 *  - `cartGetters.getItems` returns `[{}]` for an empty cart (pages/checkout.vue
 *    detects emptiness via `Object.getOwnPropertyNames(...)`).
 *
 * v2 schema notes (verified against api.helice.cloud/v2/graphql, 2026-06-08):
 *  - localized fields are arrays (`names`/`slugs`/`descriptions`/`shortNames`)
 *    → read via the SDK `getLocalized` helper.
 *  - product attributes moved from `attributeValues.items[].textValue` to
 *    `attributes.items[].{ attributeDescription, value }` where `value` is a
 *    typed union (`AttributeTextValue.textValues` / `AttributeIntValue.intValue`).
 *  - cart item key is `itemId` (was `id`); item pricing/qty/discount fields are
 *    unchanged. `media.images.items[].imageVariants[].url` is unchanged.
 */
import { getLocalized } from '@propeller-commerce/propeller-sdk-v2'
import { productSlug } from '~/utils/slug.module'
import type {
  Product,
  Cart,
  CartMainItem,
  CartAddress,
  Bundle,
  AttributeResult,
  AttributeValue,
  LocalizedString,
  MediaImage
} from '@propeller-commerce/propeller-sdk-v2'

const LANG = 'NL'

/** Examenbundel keeps its own copy of several localized fields under language
 * `EX`, alongside the generic ThiemeMeulenhoff `NL` one. */
const EXAMENBUNDEL_LANG = 'EX'

/** Description/short-description language. Examenbundel-specific copy is stored
 * under language `EX`. Only the `EX` variant is shown — deliberately NO fallback
 * to the generic `NL` (ThiemeMeulenhoff) copy: a product without `EX` shows no
 * description rather than the wrong one (EXAMS-138 QA). */
const DESCRIPTION_LANG = EXAMENBUNDEL_LANG

/** Product images are tagged with the storefront they belong to. Prefer the
 * `Examenbundel`-tagged image(s) over the generic `TMNL` ones (EXAMS-138 QA:
 * only the Examenbundel-tagged images should be published on the site). */
const PREFERRED_IMAGE_TAG = 'Examenbundel'

// Re-export SDK types under the names the app already imports from here, so
// `import { Product, Cart, CartItem, ... } from './getters'` keeps working.
export type { Product, Cart, CartAddress, Bundle, LocalizedString }
export type CartItem = CartMainItem

export interface AgnosticAttribute {
  name: string
  value: string
  label: string
}

export interface AgnosticPrice {
  regular?: number
  special?: number
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Flatten an SDK `AttributeValue` union to a display string. */
function attrValueToString (value?: AttributeValue): string {
  if (!value) { return '' }
  const v = value as unknown as Record<string, unknown>
  if (Array.isArray(v.textValues)) {
    const first = (v.textValues as { values?: string[] }[])[0]
    // Multi-value attributes come back comma-split but untrimmed on the v2
    // tenant (`["vmbo-gl", " vmbo-tl"]`), which rendered as "vmbo-gl,  vmbo-tl".
    return first?.values?.map(entry => entry.trim()).filter(Boolean).join(', ') ?? ''
  }
  if (v.intValue !== undefined) { return String(v.intValue) }
  if (v.floatValue !== undefined) { return String(v.floatValue) }
  if (v.stringValue !== undefined) { return String(v.stringValue) }
  if (v.booleanValue !== undefined) { return String(v.booleanValue) }
  if (v.enumValue !== undefined) { return String(v.enumValue) }
  return ''
}

/** Exact localized value for a language; empty string when that language is
 * absent. No fallback to another language (see `DESCRIPTION_LANG`). */
function getLocalizedStrict (values: LocalizedString[] = [], lang: string): string {
  return values.find(entry => entry.language === lang)?.value ?? ''
}

/** First non-blank localized value across `langs`, in order of preference.
 *
 * The SDK's `getLocalized` only falls through when a language is ABSENT; a
 * language that is present but holds an empty string short-circuits it. The v2
 * tenant stores exactly that — see `getShortName`. */
function getLocalizedPreferred (values: LocalizedString[] = [], langs: string[]): string {
  for (const lang of langs) {
    const value = values.find(entry => entry.language === lang)?.value?.trim()
    if (value) { return value }
  }
  return ''
}

/** Whether an image carries the given storefront tag. */
function imageHasTag (image: MediaImage, tag: string): boolean {
  return (image.tags ?? []).some(t => (t.values ?? []).includes(tag))
}

/** Displayable URL for an image. The storefront requests no image
 * transformations, so `imageVariants` is always empty — the single original
 * (`images[0].originalUrl`) is the only populated source (EXAMS-138 QA:
 * images not visible because the code only read the empty `imageVariants`). */
function imageUrl (image?: MediaImage): string {
  return image?.images?.[0]?.originalUrl ?? ''
}

/** Images to publish for the Examenbundel storefront: ONLY the
 * `Examenbundel`-tagged images, ordered by display priority (lower value shown
 * first). Deliberately NO fallback to untagged / `TMNL` images — a product with
 * no Examenbundel-tagged image shows none (EXAMS-138 QA). */
function storefrontImages (items: MediaImage[]): MediaImage[] {
  return items
    .filter(image => imageHasTag(image, PREFERRED_IMAGE_TAG))
    .sort((a, b) => (a.priority ?? 1000) - (b.priority ?? 1000))
}

// ─── productGetters ──────────────────────────────────────────────────────────

function getName (product?: Product | null): string {
  return getLocalized(product?.names ?? [], LANG) || ''
}

/** Short name shown as the second line of a product card ("Wiskunde C" under
 * "Voordeelpakket").
 *
 * The Examenbundel wording lives under `EX`; the `NL` entry is the fondsbrede
 * one and on the v2 tenant it is usually present but EMPTY — which stops
 * `getLocalized` from falling through to `EX`. Reading `NL` therefore left 39
 * of 147 products (every voordeelpakket among them) without a short name, so
 * the card fell back to the full product name: "Voordeelpakket / Examenbundel
 * voordeelpakket vwo Wiskunde C 2026/2027" (EXAMS-138, Menno 20-08-2026).
 * Where `NL` is filled it holds the generic wording — "Nederlandse taal" and
 * "Havo Scheikunde" where the shop says "Nederlands" and "Scheikunde" — so `EX`
 * wins there too. */
function getShortName (product?: Product | null): string {
  return getLocalizedPreferred(product?.shortNames ?? [], [EXAMENBUNDEL_LANG, LANG])
}

/** From the name, not the tenant slug: v2 drops the "/" that the name still has. */
function getSlug (product?: Product | null): string {
  return productSlug(getName(product)) || productSlug(getLocalized(product?.slugs ?? [], LANG))
}

function getPrice (product?: Product | null): AgnosticPrice {
  return {
    regular: product?.price?.net
  }
}

interface GalleryItem {
  small: string
  normal: string
  big: string
}

function getGallery (product?: Product | null): GalleryItem[] {
  // No size variants exist (see `imageUrl`), so small/normal/big all resolve to
  // the same original image.
  return storefrontImages(product?.media?.images?.items ?? []).map(image => {
    const url = imageUrl(image)
    return { small: url, normal: url, big: url }
  })
}

function getCoverImage (product?: Product | null): string {
  return imageUrl(storefrontImages(product?.media?.images?.items ?? [])[0])
}

function formatAttributeList (attributes: AttributeResult[]): AgnosticAttribute[] {
  return attributes.map(attr => ({
    name: attr.attributeDescription?.name ?? '',
    value: attrValueToString(attr.value),
    label: getLocalized(attr.attributeDescription?.descriptions ?? [], LANG) || ''
  }))
}

function getAttributes (products?: Product | Product[] | null, filterByAttributeName?: string[]): AgnosticAttribute[] {
  const isSingleProduct = !Array.isArray(products)
  const productList = isSingleProduct ? [products as Product] : products as Product[]
  if (!products || (isSingleProduct && !(products as Product)?.attributes) || productList.length === 0) {
    return []
  }

  const formatAttributes = (product: Product): AgnosticAttribute[] =>
    formatAttributeList(product.attributes?.items ?? []).filter(attribute =>
      filterByAttributeName ? filterByAttributeName.includes(attribute.name) : attribute
    )

  return productList
    .map(product => formatAttributes(product))
    .reduce((prev, curr) => [...prev, ...curr], [])
    .reduce<AgnosticAttribute[]>((prev, curr) => {
      const isAttributeExist = prev.some(el => el.name === curr.name && el.value === curr.value)
      return isAttributeExist ? prev : [...prev, curr]
    }, [])
}

function getDescription (product?: Product | null): string {
  return getLocalizedStrict(product?.descriptions ?? [], DESCRIPTION_LANG)
}

function getShortDescription (product?: Product | null): string {
  return getLocalizedStrict(product?.shortDescriptions ?? [], DESCRIPTION_LANG)
}

function getId (product: Product): string {
  return product.productId.toString()
}

function getStatus (product?: Product | null): string | undefined {
  return product?.status
}

function getBundleProducts (product?: Product | null): Bundle[] {
  return product?.bundles || []
}

interface CrossupsellResult {
  type: string
  subtype?: string
  item: Product
}

interface CrossupsellItem {
  type?: string
  subType?: string
  productTo?: Product
  clusterTo?: Product
}

function getCrossupsellProducts (product?: Product | null, types?: string[]): CrossupsellResult[] {
  // v2 splits v1's single `crossupsells` into two sides, and reading the wrong
  // one is why the cross/upsells set in Propeller never appeared while the
  // product recommended itself over and over (EXAMS-138, Menno 25-08):
  //
  //   crossupsellsFrom — relations configured ON this product. `productTo` is
  //                      the recommended product.  <- what we want
  //   crossupsellsTo   — relations OTHER products point at it with. `productTo`
  //                      is this product itself.
  //
  // Measured on the acceptance tenant: voordeelpakket vwo Wiskunde B (65) has 3
  // outgoing relations (Scheikunde, Natuurkunde, Biologie) and 6 incoming ones.
  //
  // Self-links and repeats are dropped as a safety net: a recommendation
  // carousel must never point at the page it is on, and the slider keys on
  // productId, so duplicates broke its dots too.
  //
  // The SDK types `productTo`/`clusterTo` as the `IBaseProduct` interface while
  // every consumer here expects a full `Product`, hence the widening cast.
  const anchorId = product?.productId
  const items = (product?.crossupsellsFrom?.items ?? []) as unknown as CrossupsellItem[]
  const seen = new Set<number>()

  return items.reduce<CrossupsellResult[]>((results, crossupsell) => {
    if (types && !types.includes(crossupsell.type ?? '')) { return results }

    const item = crossupsell.productTo ?? crossupsell.clusterTo
    if (!item?.productId || item.productId === anchorId || seen.has(item.productId)) {
      return results
    }

    seen.add(item.productId)
    results.push({
      type: crossupsell.type ?? '',
      subtype: crossupsell.subType,
      item
    })
    return results
  }, [])
}

function getInventory (product?: Product | null): number {
  return product?.inventory?.totalQuantity || 0
}

export const productGetters = {
  getName,
  getShortName,
  getSlug,
  getPrice,
  getGallery,
  getCoverImage,
  getAttributes,
  getDescription,
  getShortDescription,
  getId,
  getStatus,
  getBundleProducts,
  getCrossupsellProducts,
  getInventory
}

// ─── cartGetters ─────────────────────────────────────────────────────────────

function getItems (cart?: Cart | null): CartItem[] {
  // QUIRK (kept from v1): an empty cart yields `[{}]`, not `[]` —
  // pages/checkout.vue counts own properties of the first item to detect this.
  return cart?.items || [{} as CartItem]
}

function getItemName (item: CartItem): string {
  return getLocalized(item.product?.names ?? [], LANG) || ''
}

function getItemImage (item: CartItem): string {
  return imageUrl(storefrontImages(item.product?.media?.images?.items ?? [])[0])
}

function getItemPrice (item: CartItem): AgnosticPrice {
  return {
    regular: item.priceNet,
    special: item.priceNet - Math.abs(item.discount)
  }
}

function getItemQty (item: CartItem): number {
  return item.quantity
}

function getItemDiscount (item: CartItem): { discount: number, percentage: number } {
  return {
    discount: item.discount,
    percentage: item.discountPercentage
  }
}

/**
 * Gross (incl. VAT) line total BEFORE any incentive discount — the "was" price.
 *
 * Propeller mixes units on a cart item: `price` and `discount` are per unit and
 * EXCL. VAT, while `priceNet` and `totalPriceNet` are INCL. VAT ("Net" here means
 * incl., matching `total.subTotalNet`). So the VAT factor is derived per item
 * instead of hardcoding 9% — a cart can hold 9% and 21% lines side by side.
 *
 * Verified against a live acceptance cart (2 x Samengevat havo Aardrijkskunde):
 *   price 15.60, discount 0.78, priceNet 16.1538, totalPriceNet 32.3076
 *   -> factor 16.1538 / (15.60 - 0.78) = 1.09
 *   -> was 15.60 * 1.09 * 2 = 34.00, now 32.31, discount 1.70
 */
function getItemGrossListTotal (item: CartItem): number {
  const netUnitAfterDiscount = item.price - Math.abs(item.discount ?? 0)
  const vatFactor = netUnitAfterDiscount ? item.priceNet / netUnitAfterDiscount : 1
  return item.price * vatFactor * item.quantity
}

function getItemSku (item: CartItem): string {
  return item.product?.sku ?? ''
}

function getTotals (cart?: Cart | null): { total: number, subtotal: number, special: number } {
  return {
    total: cart?.total?.totalNet ?? 0,
    subtotal: cart?.total?.subTotalNet ?? 0,
    special: cart?.total?.subTotalNet ?? 0
  }
}

function getAppliedTaxLevels (cart?: Cart | null): unknown[] | undefined {
  return cart?.taxLevels
}

function getShippingPrice (cart?: Cart | null): number {
  // v2 renamed CartPostageData.postageNet → priceNet.
  return cart?.postageData?.priceNet || 0
}

function getTotalItems (cart?: Cart | null): number {
  return cart?.items?.length || 0
}

function getAppliedCoupon (cart?: Cart | null): { id: string, name: string, value: number, code: string } | null {
  return cart?.actionCode
    ? {
        id: cart.actionCode,
        name: cart.actionCode,
        value: 0,
        code: cart.actionCode
      }
    : null
}

function getDiscounts (cart?: Cart | null): { id: string, name: string, description: string, value: number }[] {
  return [
    {
      id: 'discount',
      name: 'discount',
      description: 'discount',
      value: cart?.total?.discountNet ?? 0
    }
  ]
}

export const cartGetters = {
  getItems,
  getItemName,
  getItemImage,
  getItemPrice,
  getItemQty,
  getItemDiscount,
  getItemGrossListTotal,
  getItemSku,
  getTotals,
  getAppliedTaxLevels,
  getShippingPrice,
  getTotalItems,
  getAppliedCoupon,
  getDiscounts
}

// ─── facetGetters ────────────────────────────────────────────────────────────

interface FacetSearchData {
  data?: { items?: unknown[] } | null
}

function getProducts (searchData?: FacetSearchData | null): unknown[] {
  if (!searchData || !searchData.data || !searchData.data.items) {
    return []
  }
  return searchData.data.items
}

export const facetGetters = {
  getProducts
}
